<template>
  <div class="w-full">
    <LTopDash
      :default-title="t('dashboard.title')"
    />

    <main class="flex flex-col bg-grey-light w-full px-4 py-6 gap-4">
      <!-- Section 1 — My Trade Shows (Key Figures) -->
      <section class="mb-3">
        <h2 class="text-heading-sm font-bold text-gray-800 mb-2">
          {{ t('dashboard.myTradeShows') }}
        </h2>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card
            v-for="figure in keyFigures"
            :key="figure.key"
            :pt="{
              body: { class: 'flex flex-row justify-between items-center gap-2 px-4' },
            }"
          >
            <template #title>
              <h3 class="heading-sm font-bold text-gray-800">{{ t(figure.label) }}</h3>
            </template>
            <template #content>
              <p class="text-2xl font-bold text-primary-500">{{ figure.count }}</p>
            </template>
          </Card>
        </div>
      </section>

      <!-- Sections 2 & 3 — Next Event + Open Tasks -->
      <div class="block">
        <div class="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-3">
          <!-- Section 2 — Next Event -->
          <section class="lg:col-span-2 flex flex-col">
            <h2 class="text-heading-sm font-bold text-gray-800 mb-2">
              {{ t('dashboard.nextEvent') }}
            </h2>
            <Card
              class="flex-1"
              :pt="{
                body: { class: 'gap-5 h-full flex flex-col' },
                content: { class: 'flex-1 flex flex-col' },
              }"
            >
              <template #content>
                <div v-if="nextEvent" class="flex flex-col gap-4 flex-1">
                  <div class="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <h3 class="text-2xl font-bold text-slate-800">
                        {{ nextEvent.name }}
                      </h3>
                      <div class="flex items-center gap-2 text-base text-gray-500 mt-3">
                        <span class="material-symbols-outlined text-xl">location_on</span>
                        <span>{{ nextEvent.location }}</span>
                      </div>
                      <div class="flex items-center gap-2 text-base text-gray-500 mt-2">
                        <span class="material-symbols-outlined text-xl">calendar_month</span>
                        <span>{{ nextEvent.dateFrom }} – {{ nextEvent.dateTo }}</span>
                      </div>
                    </div>
                    <span
                      class="inline-flex items-center gap-1.5 text-base font-medium"
                      :class="nextEvent.complete ? 'text-success-600' : 'text-error-500'"
                    >
                      <span
                        class="w-3 h-3 rounded-full"
                        :class="nextEvent.complete ? 'bg-success-300' : 'bg-error-300'"
                      ></span>
                      {{
                        nextEvent.complete
                          ? t('dashboard.planningComplete')
                          : t('dashboard.dataMissing')
                      }}
                    </span>
                  </div>

                  <div class="grid grid-cols-2 md:grid-cols-3 gap-4 pt-2">
                    <div>
                      <p class="text-xs text-gray-500 uppercase">{{ t('dashboard.countdown') }}</p>
                      <p class="text-xl font-bold text-primary-500">
                        {{ t('dashboard.daysRemaining', { count: nextEvent.daysUntil }) }}
                      </p>
                    </div>
                    <div>
                      <p class="text-xs text-gray-500 uppercase">
                        {{ t('dashboard.boothNumber') }}
                      </p>
                      <p class="text-xl font-bold text-slate-800">
                        {{ nextEvent.boothNumber || t('dashboard.boothNotAssigned') }}
                      </p>
                    </div>
                    <div>
                      <p class="text-xs text-gray-500 uppercase">
                        {{ t('dashboard.exhibitsAssigned') }}
                      </p>
                      <p class="text-xl font-bold text-slate-800">
                        {{ nextEvent.exhibitsCount }}
                      </p>
                    </div>
                  </div>

                  <div class="flex justify-start mt-auto">
                    <Button :label="t('dashboard.viewDetails')" @click="viewNextEvent" />
                  </div>
                </div>
                <div v-else class="flex flex-col items-center justify-center text-center py-10 gap-3">
                  <span class="material-symbols-outlined text-5xl text-gray-300">event_busy</span>
                  <p class="text-base font-medium text-gray-700">
                    {{ t('dashboard.noUpcomingEvent') }}
                  </p>
                  <p class="text-sm text-gray-500">
                    {{ t('dashboard.noUpcomingEventHint') }}
                  </p>
                  <Button
                    :label="t('dashboard.createNewPlan')"
                    icon="pi pi-plus"
                    @click="goToCreatePlan"
                  />
                </div>
              </template>
            </Card>
          </section>

          <!-- Section 3 — Open Tasks -->
          <section class="flex flex-col">
            <h2 class="text-heading-sm font-bold text-gray-800 mb-2">
              {{ t('dashboard.openTasks') }}
            </h2>
            <Card class="flex-1" :pt="{ body: { class: 'gap-3' } }">
              <template #content>
                <ul v-if="openTasks.length > 0" class="flex flex-col divide-y divide-gray-100">
                  <li
                    v-for="task in openTasks"
                    :key="task.id"
                    class="flex items-start gap-3 py-3 cursor-pointer hover:bg-gray-50 -mx-2 px-2 rounded-sm"
                    @click="goToTask(task)"
                  >
                    <span
                      class="material-symbols-outlined text-xl mt-0.5"
                      :class="task.urgent ? 'text-error-500' : 'text-gray-400'"
                    >
                      {{ task.icon }}
                    </span>
                    <div class="flex-1 min-w-0">
                      <p class="text-sm font-medium text-gray-800 truncate">{{ task.context }}</p>
                      <p class="text-xs text-gray-500 truncate">
                        {{ t(task.descriptionKey) }}
                      </p>
                    </div>
                    <span v-if="task.due" class="text-xs text-gray-500 whitespace-nowrap">
                      {{ t('dashboard.due') }}: {{ task.due }}
                    </span>
                  </li>
                </ul>
                <div v-else class="flex flex-col items-center justify-center text-center py-10 gap-2">
                  <span class="material-symbols-outlined text-4xl text-success-300">
                    task_alt
                  </span>
                  <p class="text-sm text-gray-500">{{ t('dashboard.noOpenTasks') }}</p>
                </div>
              </template>
            </Card>
          </section>
        </div>
      </div>
      <!-- Section 4 — Master Data Status -->
      <section>
        <h2 class="text-heading-sm font-bold text-gray-800 mb-3">
          {{ t('dashboard.masterDataStatus') }}
        </h2>
        <div class="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card
            v-for="tile in masterDataTiles"
            :key="tile.key"
            class="cursor-pointer hover:shadow-md transition-shadow"
            :pt="{ body: { class: 'flex flex-row justify-between items-center gap-2 px-4' } }"
            @click="tile.onClick"
          >
            <template #title>
              <h3 class="text-sm font-semibold text-gray-700">{{ t(tile.label) }}</h3>
            </template>
            <template #content>
              <p class="text-2xl font-bold text-primary-500">{{ tile.count }}</p>
            </template>
          </Card>
        </div>
      </section>

      <!-- Section 5 — Quick Actions -->
      <section>
        <h2 class="text-heading-sm font-bold text-gray-800 mb-3">
          {{ t('dashboard.quickActions') }}
        </h2>
        <Card>
          <template #content>
            <div class="flex flex-wrap gap-3">
              <Button
                :label="t('dashboard.createNewPlan')"
                icon="pi pi-plus"
                @click="goToCreatePlan"
              />
              <Button
                :label="t('dashboard.inviteTeamMember')"
                icon="pi pi-user-plus"
                severity="secondary"
                outlined
                @click="goToInviteTeamMember"
              />
            </div>
          </template>
        </Card>
      </section>
    </main>
  </div>
</template>

<script setup lang="ts">
import LTopDash from '@/components/global/LTopDash.vue'
import Card from 'primevue/card'
import Button from 'primevue/button'
import moment from 'moment'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import DashboardService, {
  type IPlanningCounts,
  type INextEvent,
  type IOpenTask,
  type IMasterData,
} from '@/services/dashboardServices'

const { t } = useI18n()
const router = useRouter()

const planningCounts = ref<IPlanningCounts>({ ongoing: 0, upcoming: 0, past: 0 })

const keyFigures = computed(() => [
  { key: 'ongoing', label: 'dashboard.ongoing', count: planningCounts.value.ongoing },
  { key: 'upcoming', label: 'dashboard.upcoming', count: planningCounts.value.upcoming },
  { key: 'past', label: 'dashboard.past', count: planningCounts.value.past },
])

const nextEventRaw = ref<INextEvent | null>(null)

const nextEvent = computed(() => {
  const e = nextEventRaw.value
  if (!e) return null
  return {
    id: e.id,
    name: e.planning_name || '—',
    location: e.location || '—',
    dateFrom: e.exhibition_start_date ? moment(e.exhibition_start_date).format('DD.MM.YYYY') : '—',
    dateTo: e.exhibition_end_date ? moment(e.exhibition_end_date).format('DD.MM.YYYY') : '—',
    daysUntil: e.days_until_setup,
    boothNumber: e.booth_number || '',
    exhibitsCount: e.items_count,
    complete: e.is_complete,
  }
})

onMounted(async () => {
  const [counts, event, tasks, master] = await Promise.all([
    DashboardService.getPlanningCounts(),
    DashboardService.getNextEvent(),
    DashboardService.getOpenTasks(),
    DashboardService.getMasterData(),
  ])
  planningCounts.value = counts
  nextEventRaw.value = event
  openTasksRaw.value = tasks
  masterData.value = master
})

interface OpenTask {
  id: string
  descriptionKey: string
  context: string
  icon: string
  urgent?: boolean
  due?: string
  link?: { name: string; params?: Record<string, string | number> }
}

const openTasksRaw = ref<IOpenTask[]>([])

const TASK_TYPE_META: Record<string, { descriptionKey: string; icon: string }> = {
  incomplete_plan: { descriptionKey: 'dashboard.taskIncompletePlan', icon: 'edit_note' },
  unassigned_exhibits: {
    descriptionKey: 'dashboard.taskUnassignedExhibits',
    icon: 'inventory_2',
  },
  pending_invitation: {
    descriptionKey: 'dashboard.taskPendingInvitation',
    icon: 'mail',
  },
}

const openTasks = computed<OpenTask[]>(() =>
  openTasksRaw.value.map((task, idx) => {
    const meta = TASK_TYPE_META[task.type] ?? {
      descriptionKey: 'dashboard.taskIncompletePlan',
      icon: 'warning',
    }
    const dueMoment = task.due_date ? moment(task.due_date) : null
    const daysUntilDue = dueMoment ? dueMoment.startOf('day').diff(moment().startOf('day'), 'days') : null
    return {
      id: `${task.type}-${task.planning_id}-${idx}`,
      descriptionKey: meta.descriptionKey,
      context: task.title,
      icon: meta.icon,
      urgent: daysUntilDue !== null && daysUntilDue <= 7,
      due: dueMoment ? dueMoment.format('DD.MM.YYYY') : undefined,
      link: task.planning_id
        ? { name: 'plan-edit', params: { id: task.planning_id } }
        : undefined,
    }
  }),
)

const masterData = ref<IMasterData>({
  active_projects: 0,
  items: 0,
  categories: 0,
  active_team_members: 0,
})

const masterDataTiles = computed(() => [
  {
    key: 'active_projects',
    label: 'dashboard.activeProjects',
    count: masterData.value.active_projects,
    onClick: () => router.push({ name: 'my-plans' }),
  },
  {
    key: 'exhibits',
    label: 'dashboard.exhibits',
    count: masterData.value.items,
    onClick: () => router.push({ name: 'items' }),
  },
  {
    key: 'categories',
    label: 'dashboard.categories',
    count: masterData.value.categories,
    // Categories are maintained on the articles page, not on the tags screen
    // this used to open (F-24). Label and count deliberately unchanged.
    onClick: () => router.push({ name: 'items' }),
  },
  {
    key: 'team',
    label: 'dashboard.teamMembers',
    count: masterData.value.active_team_members,
    onClick: () => router.push({ name: 'users' }),
  },
])

function goToCreatePlan() {
  router.push({ name: 'plan-create' })
}

function goToCreateExhibit() {
  // TODO — point at exhibit-create route once it exists
  router.push({ name: 'items' })
}

function goToInviteTeamMember() {
  // TODO — point at invite flow once it exists
  router.push({ name: 'users' })
}

function viewNextEvent() {
  const event = nextEvent.value
  if (!event) return
  router.push({ name: 'plan-edit', params: { id: event.id }, query: { step: '3' } })
}

function goToTask(task: OpenTask) {
  if (task.link) router.push(task.link)
}
</script>
